// School Structure Store - SAT V4 Zone
import { create } from 'zustand';
import type {
  Building,
  Room,
  Period,
  DayStructure,
  CurriculumPlan,
  Subject,
  OptionBlock,
  Set as SetEntity,
  Band,
  ValidationResult,
  YearGroup,
  DayWeekFormatData,
  LunchStructureData,
  Session,
  LunchSession,
  YearGroupString,
  DayOfWeek,
  WeekName,
} from '../types';
import {
  mockBuildings,
  mockRooms,
  mockPeriods,
  mockDayStructures,
  mockCurriculumPlans,
  mockSubjects,
  mockOptionBlocks,
  mockSets,
  mockBands,
  mockDayWeekFormatData,
  mockLunchStructureData,
} from '../lib/mockData';

// Helper to calculate end time
const calculateEndTime = (startTime: string, durationMinutes: number): string => {
  const [hours, minutes] = startTime.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes + durationMinutes;
  const endHours = Math.floor(totalMinutes / 60);
  const endMinutes = totalMinutes % 60;
  return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
};

interface SchoolStructureState {
  // Data
  buildings: Building[];
  rooms: Room[];
  periods: Period[];
  dayStructures: DayStructure[];
  curriculumPlans: CurriculumPlan[];
  subjects: Subject[];
  optionBlocks: OptionBlock[];
  sets: SetEntity[];
  bands: Band[];
  validationResults: ValidationResult[];

  // New Session Data Structures
  dayWeekFormatData: DayWeekFormatData;
  lunchStructureData: LunchStructureData;

  // Custom session types (user-defined)
  customSessionTypes: string[];

  // Filters
  selectedYearGroup: YearGroup | null;

  // Actions
  setSelectedYearGroup: (yearGroup: YearGroup | null) => void;
  addRoom: (room: Room) => void;
  updateRoom: (id: string, updates: Partial<Room>) => void;
  deleteRoom: (id: string) => void;
  addBuilding: (building: Building) => void;
  addPeriod: (period: Period) => void;
  updatePeriod: (id: string, updates: Partial<Period>) => void;
  updateCurriculumPlan: (id: string, updates: Partial<CurriculumPlan>) => void;
  addSubject: (subject: Subject) => void;
  updateSubject: (id: string, updates: Partial<Subject>) => void;
  runValidation: (yearGroup?: YearGroup) => void;

  // Day/Week Format Session Actions
  addSession: (
    yearGroup: YearGroupString,
    scheduleType: 'Bi-Weekly' | 'Weekly',
    week: WeekName | null,
    day: DayOfWeek,
    session: Session
  ) => void;
  updateSession: (
    yearGroup: YearGroupString,
    scheduleType: 'Bi-Weekly' | 'Weekly',
    week: WeekName | null,
    day: DayOfWeek,
    sessionId: string,
    updates: Partial<Session>
  ) => void;
  deleteSession: (
    yearGroup: YearGroupString,
    scheduleType: 'Bi-Weekly' | 'Weekly',
    week: WeekName | null,
    day: DayOfWeek,
    sessionId: string
  ) => void;

  // Lunch Structure Session Actions
  addLunchSession: (
    scheduleType: 'Bi-Weekly' | 'Weekly',
    week: WeekName | null,
    day: DayOfWeek,
    session: LunchSession
  ) => void;
  updateLunchSession: (
    scheduleType: 'Bi-Weekly' | 'Weekly',
    week: WeekName | null,
    day: DayOfWeek,
    sessionId: string,
    updates: Partial<LunchSession>
  ) => void;
  deleteLunchSession: (
    scheduleType: 'Bi-Weekly' | 'Weekly',
    week: WeekName | null,
    day: DayOfWeek,
    sessionId: string
  ) => void;

  // Custom Session Types Actions
  addCustomSessionType: (type: string) => void;
  removeCustomSessionType: (type: string) => void;
}

export const useSchoolStructureStore = create<SchoolStructureState>((set, get) => ({
  // Initialize with mock data
  buildings: mockBuildings,
  rooms: mockRooms,
  periods: mockPeriods,
  dayStructures: mockDayStructures,
  curriculumPlans: mockCurriculumPlans,
  subjects: mockSubjects,
  optionBlocks: mockOptionBlocks,
  sets: mockSets,
  bands: mockBands,
  validationResults: [],

  // New Session Data
  dayWeekFormatData: mockDayWeekFormatData,
  lunchStructureData: mockLunchStructureData,
  customSessionTypes: [],

  selectedYearGroup: null,

  setSelectedYearGroup: (yearGroup) => set({ selectedYearGroup: yearGroup }),

  addRoom: (room) =>
    set((state) => ({
      rooms: [...state.rooms, room],
    })),

  updateRoom: (id, updates) =>
    set((state) => ({
      rooms: state.rooms.map((r) => (r.id === id ? { ...r, ...updates } : r)),
    })),

  deleteRoom: (id) =>
    set((state) => ({
      rooms: state.rooms.filter((r) => r.id !== id),
    })),

  addBuilding: (building) =>
    set((state) => ({
      buildings: [...state.buildings, building],
    })),

  addPeriod: (period) =>
    set((state) => ({
      periods: [...state.periods, period],
    })),

  updatePeriod: (id, updates) =>
    set((state) => ({
      periods: state.periods.map((p) => (p.id === id ? { ...p, ...updates } : p)),
    })),

  updateCurriculumPlan: (id, updates) =>
    set((state) => ({
      curriculumPlans: state.curriculumPlans.map((c) =>
        c.id === id ? { ...c, ...updates } : c
      ),
    })),

  addSubject: (subject) =>
    set((state) => ({
      subjects: [...state.subjects, subject],
    })),

  updateSubject: (id, updates) =>
    set((state) => ({
      subjects: state.subjects.map((s) => (s.id === id ? { ...s, ...updates } : s)),
    })),

  runValidation: (yearGroup) => {
    const state = get();
    const results: ValidationResult[] = [];

    // Check curriculum completeness
    const plans = yearGroup
      ? state.curriculumPlans.filter((c) => c.yearGroup === yearGroup)
      : state.curriculumPlans;

    for (const plan of plans) {
      if (plan.subjects.length === 0) {
        results.push({
          id: `val-${plan.id}-no-subjects`,
          category: 'Curriculum',
          severity: 'error',
          message: `Year ${plan.yearGroup} curriculum has no subjects assigned`,
          affectedItems: [plan.id],
          suggestion: 'Add subjects to the curriculum plan',
        });
      }
    }

    // Check room availability
    const totalCapacity = state.rooms.reduce((sum, r) => sum + r.capacity, 0);
    if (totalCapacity < 750) {
      results.push({
        id: 'val-room-capacity',
        category: 'Rooms',
        severity: 'warning',
        message: 'Total room capacity may be insufficient for all pupils',
        affectedItems: state.rooms.map((r) => r.id),
        suggestion: 'Consider adding more rooms or increasing capacity',
      });
    }

    set({ validationResults: results });
  },

  // Day/Week Format Session Actions
  addSession: (yearGroup, scheduleType, week, day, session) =>
    set((state) => {
      const newData = { ...state.dayWeekFormatData };
      const yearData = { ...newData[yearGroup] };

      if (scheduleType === 'Bi-Weekly' && week) {
        const biWeekly = { ...yearData['Bi-Weekly'] };
        const weekData = { ...biWeekly[week] };
        weekData[day] = [...weekData[day], session];
        biWeekly[week] = weekData;
        yearData['Bi-Weekly'] = biWeekly;
      } else {
        const weekly = { ...yearData['Weekly'] };
        weekly[day] = [...weekly[day], session];
        yearData['Weekly'] = weekly;
      }

      newData[yearGroup] = yearData;
      return { dayWeekFormatData: newData };
    }),

  updateSession: (yearGroup, scheduleType, week, day, sessionId, updates) =>
    set((state) => {
      const newData = { ...state.dayWeekFormatData };
      const yearData = { ...newData[yearGroup] };

      const updateSessionInArray = (sessions: Session[]): Session[] =>
        sessions.map((s) =>
          s.id === sessionId
            ? {
                ...s,
                ...updates,
                endTime: updates.startTime || updates.duration
                  ? calculateEndTime(
                      updates.startTime ?? s.startTime,
                      updates.duration ?? s.duration
                    )
                  : s.endTime,
              }
            : s
        );

      if (scheduleType === 'Bi-Weekly' && week) {
        const biWeekly = { ...yearData['Bi-Weekly'] };
        const weekData = { ...biWeekly[week] };
        weekData[day] = updateSessionInArray(weekData[day]);
        biWeekly[week] = weekData;
        yearData['Bi-Weekly'] = biWeekly;
      } else {
        const weekly = { ...yearData['Weekly'] };
        weekly[day] = updateSessionInArray(weekly[day]);
        yearData['Weekly'] = weekly;
      }

      newData[yearGroup] = yearData;
      return { dayWeekFormatData: newData };
    }),

  deleteSession: (yearGroup, scheduleType, week, day, sessionId) =>
    set((state) => {
      const newData = { ...state.dayWeekFormatData };
      const yearData = { ...newData[yearGroup] };

      if (scheduleType === 'Bi-Weekly' && week) {
        const biWeekly = { ...yearData['Bi-Weekly'] };
        const weekData = { ...biWeekly[week] };
        weekData[day] = weekData[day].filter((s) => s.id !== sessionId);
        biWeekly[week] = weekData;
        yearData['Bi-Weekly'] = biWeekly;
      } else {
        const weekly = { ...yearData['Weekly'] };
        weekly[day] = weekly[day].filter((s) => s.id !== sessionId);
        yearData['Weekly'] = weekly;
      }

      newData[yearGroup] = yearData;
      return { dayWeekFormatData: newData };
    }),

  // Lunch Structure Session Actions
  addLunchSession: (scheduleType, week, day, session) =>
    set((state) => {
      const newData = { ...state.lunchStructureData };

      if (scheduleType === 'Bi-Weekly' && week) {
        const biWeekly = { ...newData['Bi-Weekly'] };
        const weekData = { ...biWeekly[week] };
        weekData[day] = [...weekData[day], session];
        biWeekly[week] = weekData;
        newData['Bi-Weekly'] = biWeekly;
      } else {
        const weekly = { ...newData['Weekly'] };
        weekly[day] = [...weekly[day], session];
        newData['Weekly'] = weekly;
      }

      return { lunchStructureData: newData };
    }),

  updateLunchSession: (scheduleType, week, day, sessionId, updates) =>
    set((state) => {
      const newData = { ...state.lunchStructureData };

      const updateSessionInArray = (sessions: LunchSession[]): LunchSession[] =>
        sessions.map((s) =>
          s.id === sessionId
            ? {
                ...s,
                ...updates,
                endTime: updates.startTime || updates.duration
                  ? calculateEndTime(
                      updates.startTime ?? s.startTime,
                      updates.duration ?? s.duration
                    )
                  : s.endTime,
              }
            : s
        );

      if (scheduleType === 'Bi-Weekly' && week) {
        const biWeekly = { ...newData['Bi-Weekly'] };
        const weekData = { ...biWeekly[week] };
        weekData[day] = updateSessionInArray(weekData[day]);
        biWeekly[week] = weekData;
        newData['Bi-Weekly'] = biWeekly;
      } else {
        const weekly = { ...newData['Weekly'] };
        weekly[day] = updateSessionInArray(weekly[day]);
        newData['Weekly'] = weekly;
      }

      return { lunchStructureData: newData };
    }),

  deleteLunchSession: (scheduleType, week, day, sessionId) =>
    set((state) => {
      const newData = { ...state.lunchStructureData };

      if (scheduleType === 'Bi-Weekly' && week) {
        const biWeekly = { ...newData['Bi-Weekly'] };
        const weekData = { ...biWeekly[week] };
        weekData[day] = weekData[day].filter((s) => s.id !== sessionId);
        biWeekly[week] = weekData;
        newData['Bi-Weekly'] = biWeekly;
      } else {
        const weekly = { ...newData['Weekly'] };
        weekly[day] = weekly[day].filter((s) => s.id !== sessionId);
        newData['Weekly'] = weekly;
      }

      return { lunchStructureData: newData };
    }),

  // Custom Session Types Actions
  addCustomSessionType: (type) =>
    set((state) => ({
      customSessionTypes: state.customSessionTypes.includes(type)
        ? state.customSessionTypes
        : [...state.customSessionTypes, type],
    })),

  removeCustomSessionType: (type) =>
    set((state) => ({
      customSessionTypes: state.customSessionTypes.filter((t) => t !== type),
    })),
}));

export default useSchoolStructureStore;
